/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ChoiceGroup, IChoiceGroupOption, IChoiceGroupStyleProps, IChoiceGroupStyles } from '@fluentui/react/lib/ChoiceGroup';
import { IDropdownOption } from '@fluentui/react/lib/components/Dropdown';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextFieldStyleProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CluTrainedModelStore } from 'src/clu/core/stores/CluTrainedModelStore';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { ResourceAssignmentForRegionSwagger } from 'src/core/services/resourceAssignmentSwaggers';
import { CustomSkillDeploymentStore } from 'src/core/stores/CustomSkillDeploymentStore';
import { ResourceAssignmentStore } from 'src/core/stores/ResourceAssignmentStore';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { CustomTextTrainedModelStore } from 'src/customText/core/stores/CustomTextTrainedModelStore';
import { DeploymentType } from 'src/modules/modelDeploy/DeployModelModal';

export type DeploymentInfoScreenProps = {
    deploymentName: string;
    trainedModelName: string;
    isServiceKeyTypeValid: boolean;
    regions?: ResourceAssignmentStore[];
    deploymentNameErrorMessage?: string;
    selectedDeploymentType: DeploymentType;
    isMultiRegionDeploymentEnabled?: boolean;
    onChangeDeploymentName: (newName: string) => void;
    onChangeTrainedModelName: (newName: string) => void;
    isOrchestrationProjectConnectedToLUISOrQna: boolean;
    onDeploymentTypeChange: (newType: DeploymentType) => void;
    deployments: (CustomSkillDeploymentStore | CustomSkillDeploymentStore)[];
    trainedModels: (CluTrainedModelStore | CustomTextTrainedModelStore)[];
    onChangeResourceIds?: (selectedResources: ResourceAssignmentForRegionSwagger[]) => void;
};

const messages = defineMessages({
    modelLabel: { id: 'modelDeploy.deployModelModal.modelLabel', defaultMessage: 'Model' },
    deploymentLabel: { id: 'modelDeploy.deployModelModal.deploymentLabel', defaultMessage: 'Deployment name' },
    newOptionText: { id: 'modelDeploy.deployModelModal.newOptionText', defaultMessage: 'Create a new deployment name' },
    modelNamePlaceholder: { id: 'modelDeploy.deployModelModal.modelNamePlaceholder', defaultMessage: 'Select a trained model' },
    existingOptionText: { id: 'modelDeploy.deployModelModal.existingOptionText', defaultMessage: 'Overwrite an existing deployment name' },
    deploymentNamePlaceholder: { id: 'modelDeploy.deployModelModal.deploymentNamePlaceholder', defaultMessage: 'Enter a deployment name' },
    assignModelTitle: {
        id: 'modelDeploy.deployModelModal.assignModelTitle',
        defaultMessage: 'Assign trained model to your deployment name'
    },
    assignModelDescription: {
        id: 'modelDeploy.deployModelModal.assignModelDescription',
        defaultMessage: 'Add a trained model to your selected deployment name  '
    },
    deploymentNameSelectionPlaceholder: {
        id: 'modelDeploy.deployModelModal.deploymentNameSelectionPlaceholder',
        defaultMessage: 'Select a deployment'
    },
    deploymentOptionsDescription: {
        id: 'modelDeploy.deployModelModal.deploymentOptionsDescription',
        defaultMessage: 'You can create a new deployment name or overwrite an existing deployment name to add a trained model to them'
    },
    existingDeploymentTypeDescription: {
        id: 'modelDeploy.deployModelModal.existingDeploymentTypeDescription',
        defaultMessage: 'By selecting an existing deployment name, you will override any deployed model to this deployment name'
    },
    deploymentRegionsPlaceholder: {
        id: 'modelDeploy.deployModelModal.deploymentRegionsPlaceholder',
        defaultMessage: 'Select deployment region(s)'
    },
    deploymentRegionsTitle: {
        id: 'modelDeploy.deployModelModal.deploymentRegionsTitle',
        defaultMessage: 'Deployment regions'
    },
    deploymentRegionsDescription: {
        id: 'modelDeploy.deployModelModal.deploymentRegionsDescription',
        defaultMessage: 'Select the regions to deploy in based on the available assigned resources.'
    },
    deploymentRegionsLabel: {
        id: 'modelDeploy.deployModelModal.deploymentRegionsLabel',
        defaultMessage: 'Regions'
    },
    invalidServiceKeyMessage: {
        id: 'modelDeploy.deployModelModal.invalidServiceKeyMessage',
        defaultMessage: `You must assign yourself the role "Cognitive Services Language Owner" to your Language resource "{currentResourceName}".`
    },
    learnMore: {
        id: 'modelDeploy.deployModelModal.learnMore',
        defaultMessage: 'Learn more'
    },
    invalidOrchestrationProject: {
        id: 'modelDeploy.deployModelModal.invalidOrchestrationProject',
        defaultMessage:
            'You cannot select a deployment resource to a project with a question answering or LUIS intent connection. Remove those connections from your project to add deployment resources.'
    }
});

const textFieldStyles = classNamesFunction<ITextFieldStyleProps, ITextFieldStyles>()({ root: { paddingLeft: '26px', minWidth: 320 } });
const choiceGroupStyles = classNamesFunction<IChoiceGroupStyleProps, IChoiceGroupStyles>()({ root: { marginBottom: 20, paddingLeft: 2 } });

export const DeploymentInfoScreen = observer((props: DeploymentInfoScreenProps) => {
    const {
        deployments,
        regions = [],
        trainedModels,
        deploymentName,
        trainedModelName,
        onChangeResourceIds,
        isServiceKeyTypeValid,
        selectedDeploymentType,
        onChangeDeploymentName,
        onDeploymentTypeChange,
        onChangeTrainedModelName,
        deploymentNameErrorMessage,
        isOrchestrationProjectConnectedToLUISOrQna,
        isMultiRegionDeploymentEnabled = FeatureToggles.MultiRegionDeployments,
    } = props;

    const { rootStore } = useGlobalState();
    const { localize } = useLocalization();

    const { path: currentResourceId, name: currentResourceName } = rootStore.azureStore.session.csAccountStore;
    const isResourceAssignmentDisabled = !isServiceKeyTypeValid || isOrchestrationProjectConnectedToLUISOrQna;

    const [selectedResources, setSelectedResources] = React.useState<ResourceAssignmentForRegionSwagger[]>([]);

    const deploymentRegionsOptions: IDropdownOption[] = (isResourceAssignmentDisabled
        ? regions.filter(item => item.azureResourceId === currentResourceId)
        : regions.filter(item => Boolean(item.azureResourceId))
    ).map(item => ({
        selected: true,
        key: item.regionName,
        text: `${item.regionName} - ${item.resourceName}`,
        data: { azureResourceId: item.azureResourceId },
        disabled: item.azureResourceId === currentResourceId
    }));

    React.useEffect(() => {
        onChangeResourceIds(
            selectedResources.filter(resource => resource.azureResourceId !== currentResourceId && resource.region !== 'selectAll')
        );
    }, [selectedResources]);

    const deploymentNameOptions: IChoiceGroupOption[] = [
        {
            key: 'new',
            text: localize(messages.newOptionText),
            onRenderLabel: (fieldProps, defaultRenderer) => (
                <Stack tokens={{ childrenGap: 8 }}>
                    {defaultRenderer(fieldProps)}
                    {selectedDeploymentType === 'new' && (
                        <LuisTextField
                            aria-required
                            value={deploymentName}
                            styles={textFieldStyles}
                            data-automation-id="deploymentName"
                            errorMessage={deploymentNameErrorMessage}
                            placeholder={localize(messages.deploymentNamePlaceholder)}
                            onChange={(_, newValue) => onChangeDeploymentName(newValue)}
                        />
                    )}
                </Stack>
            )
        },
        {
            key: 'existing',
            text: localize(messages.existingOptionText),
            onRenderLabel: (fieldProps, defaultRenderer) => (
                <Stack tokens={{ childrenGap: 8 }}>
                    <Stack tokens={{ childrenGap: 3 }}>
                        {defaultRenderer(fieldProps)}
                        <LuisText styles={{ root: { paddingLeft: '26px' } }}>
                            {localize(messages.existingDeploymentTypeDescription)}
                        </LuisText>
                    </Stack>
                    {selectedDeploymentType === 'existing' && (
                        <LuisDropdown
                            aria-required
                            selectedKey={deploymentName}
                            label={localize(messages.deploymentLabel)}
                            styles={{ root: { paddingLeft: 26, maxWidth: 320 } }}
                            onChange={(_, opt) => onChangeDeploymentName(opt.key as string)}
                            placeholder={localize(messages.deploymentNameSelectionPlaceholder)}
                            calloutProps={{ styles: { calloutMain: { maxHeight: '350px !important' } } }}
                            options={deployments.map(d => ({ key: d.deploymentName, text: d.deploymentName }))}
                        />
                    )}
                </Stack>
            )
        }
    ];

    const handleRegionChange = (option: IDropdownOption) => {
        const selectedOptionResource = selectedResources.find(resource => resource.region === (option.key as string));

        if (option.selected) {
            if (!selectedOptionResource) {
                setSelectedResources([
                    ...selectedResources,
                    { region: option.key as string, azureResourceId: (option.data as { azureResourceId: string })?.azureResourceId }
                ]);
            }
        } else {
            selectedResources.splice(selectedResources.indexOf(selectedOptionResource), 1);
            setSelectedResources([...selectedResources]);
        }
    };

    const handleSelectAllRegions = (isSelectAllSelected: boolean, options: IDropdownOption[]) => {
        if (isSelectAllSelected) {
            setSelectedResources([
                ...options.map(option => ({
                    region: option.key as string,
                    azureResourceId: (option.data as { azureResourceId: string })?.azureResourceId
                }))
            ]);
        } else {
            setSelectedResources(
                options
                    .filter(option => option.disabled)
                    .map(option => ({
                        region: option.key as string,
                        azureResourceId: (option.data as { azureResourceId: string })?.azureResourceId
                    }))
            );
        }
    };

    const getAADTooltipMessage = (
        <LuisText>
            {`${localize(messages.invalidServiceKeyMessage, { currentResourceName })} `}
            <ExternalLink isDarkMode href="https://aka.ms/rbac-language">
                {localize(messages.learnMore)}
            </ExternalLink>
        </LuisText>
    );

    const getInvalidOrchestrationProjectTooltipMessage = (
        <LuisText>
            {`${localize(messages.invalidOrchestrationProject)} `}
            <ExternalLink isDarkMode href="https://aka.ms/orchestration-deployment">
                {localize(messages.learnMore)}
            </ExternalLink>
        </LuisText>
    );

    return (
        <Stack tokens={{ childrenGap: 15 }}>
            <Stack tokens={{ childrenGap: 5 }}>
                <LuisText>{localize(messages.deploymentOptionsDescription)}</LuisText>

                <ChoiceGroup
                    styles={choiceGroupStyles}
                    options={deploymentNameOptions}
                    selectedKey={selectedDeploymentType}
                    onChange={(_, opt) => onDeploymentTypeChange(opt.key as DeploymentType)}
                />
            </Stack>

            <Stack tokens={{ childrenGap: 5 }}>
                <LuisText variant="mediumPlus" fontWeight={600}>
                    {localize(messages.assignModelTitle)}
                </LuisText>
                <LuisText>{localize(messages.assignModelDescription)}</LuisText>
                <LuisDropdown
                    selectedKey={trainedModelName}
                    styles={{ root: { maxWidth: 320 } }}
                    label={localize(messages.modelLabel)}
                    placeholder={localize(messages.modelNamePlaceholder)}
                    options={trainedModels.map(m => ({ key: m.name, text: m.name }))}
                    onChange={(_, opt) => onChangeTrainedModelName(opt.key as string)}
                    calloutProps={{ styles: { calloutMain: { maxHeight: '350px !important' } } }}
                />
            </Stack>

            {isMultiRegionDeploymentEnabled && (
                <Stack tokens={{ childrenGap: 5 }}>
                    <LuisText variant="mediumPlus" fontWeight={600}>
                        {localize(messages.deploymentRegionsTitle)}
                    </LuisText>
                    <LuisText>{localize(messages.deploymentRegionsDescription)}</LuisText>
                    <LuisTooltip
                        directionalHint={0}
                        hidden={!isResourceAssignmentDisabled}
                        content={
                            isOrchestrationProjectConnectedToLUISOrQna ? getInvalidOrchestrationProjectTooltipMessage : getAADTooltipMessage
                        }>
                        <LuisDropdown
                            options={deploymentRegionsOptions}
                            styles={{ root: { maxWidth: 320 } }}
                            disabled={isResourceAssignmentDisabled}
                            onSelectAllItems={handleSelectAllRegions}
                            onChange={(_, opt) => handleRegionChange(opt)}
                            multiSelect={!isResourceAssignmentDisabled}
                            label={localize(messages.deploymentRegionsLabel)}
                            isSelectAllEnabled={!isResourceAssignmentDisabled}
                            isSelectAllSelected={!isResourceAssignmentDisabled}
                            placeholder={localize(messages.deploymentRegionsPlaceholder)}
                            selectedKeys={selectedResources.map(resource => resource.region)}
                            calloutProps={{ styles: { calloutMain: { maxHeight: '350px !important' } } }}
                        />
                    </LuisTooltip>
                </Stack>
            )}
        </Stack>
    );
});
